import { useState, useEffect, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import {
    DeviceReplacementRequest,
    DeviceState,
    PairedDevice,
    UploadProgressPayload,
    UseCancamSocketReturn,
} from 'api/cancam/types';

const CANCAM_NAMESPACE = '/cancam';
const RECONNECT_DELAY = 3000;
const MAX_RECONNECT_ATTEMPTS = 5;

export function useCancamSocket(): UseCancamSocketReturn {
    const [isConnected, setIsConnected] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [deviceState, setDeviceState] = useState<DeviceState | null>(null);
    const [uploadProgress, setUploadProgress] = useState<UploadProgressPayload | null>(null);

    // Patrol-based pairing state (replaces isPaired/pairedDevice)
    const [patrolPairingCode, setPatrolPairingCode] = useState<string | null>(null);
    const [patrolCodeExpiry, setPatrolCodeExpiry] = useState<number | null>(null);
    const [connectedDevice, setConnectedDevice] = useState<PairedDevice | null>(null);
    const [watchingPatrolId, setWatchingPatrolId] = useState<number | null>(null);
    const [pendingReplacement, setPendingReplacement] = useState<DeviceReplacementRequest | null>(null);

    const socketRef = useRef<Socket | null>(null);
    const reconnectAttemptsRef = useRef(0);
    const codeExpiryTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Initialize socket connection
    useEffect(() => {
        const socket = io(CANCAM_NAMESPACE, {
            query: { type: 'guard' },
            transports: ['polling', 'websocket'],  // Start with polling, upgrade to websocket
            reconnection: true,
            reconnectionDelay: RECONNECT_DELAY,
            reconnectionAttempts: MAX_RECONNECT_ATTEMPTS,
        });

        socketRef.current = socket;

        // Connection events
        socket.on('connect', () => {
            console.log('[CANcam] Connected to server');
            setIsConnected(true);
            setError(null);
            reconnectAttemptsRef.current = 0;
        });

        socket.on('disconnect', (reason) => {
            console.log('[CANcam] Disconnected:', reason);
            setIsConnected(false);
            // Don't clear patrol state on temporary disconnects
            if (reason === 'io server disconnect') {
                setConnectedDevice(null);
                setDeviceState(null);
                setWatchingPatrolId(null);
            }
        });

        socket.on('connect_error', (err) => {
            console.error('[CANcam] Connection error:', err);
            setError('Failed to connect to CANcam service');
            reconnectAttemptsRef.current++;
            if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
                setError('Unable to connect to CANcam service. Please try again later.');
            }
        });

        // Patrol code generated
        socket.on('patrol_code_generated', (data: { code: string; expiresIn: number; commentListId: number }) => {
            console.log('[CANcam] Patrol code generated:', data.code);
            setPatrolPairingCode(data.code);
            setPatrolCodeExpiry(data.expiresIn);

            // Clear any existing timer
            if (codeExpiryTimerRef.current) {
                clearInterval(codeExpiryTimerRef.current);
            }

            // Start countdown timer
            codeExpiryTimerRef.current = setInterval(() => {
                setPatrolCodeExpiry((prev) => {
                    if (prev === null || prev <= 1) {
                        if (codeExpiryTimerRef.current) {
                            clearInterval(codeExpiryTimerRef.current);
                            codeExpiryTimerRef.current = null;
                        }
                        setPatrolPairingCode(null);
                        return null;
                    }
                    return prev - 1;
                });
            }, 1000);
        });

        // Device connected to patrol
        socket.on('device_connected', (data: { deviceId: string; deviceName?: string }) => {
            console.log('[CANcam] Device connected to patrol:', data);
            setConnectedDevice({
                deviceId: data.deviceId,
                deviceName: data.deviceName,
            });
            setPatrolPairingCode(null); // Clear code once device connects
            setPatrolCodeExpiry(null);
            if (codeExpiryTimerRef.current) {
                clearInterval(codeExpiryTimerRef.current);
                codeExpiryTimerRef.current = null;
            }
            setError(null);
        });

        // Device replacement request
        socket.on('device_replacement_request', (data: DeviceReplacementRequest) => {
            console.log('[CANcam] Device replacement requested:', data);
            setPendingReplacement(data);
        });

        // Device state events
        socket.on('device_state', (data: DeviceState & { deviceId: string }) => {
            setDeviceState({
                battery: data.battery,
                isRecording: data.isRecording,
                pendingUploads: data.pendingUploads,
                currentRecordingFileName: data.currentRecordingFileName,
                recordingDuration: data.recordingDuration,
            });
        });

        // Device disconnected from patrol
        socket.on('device_disconnected', () => {
            console.log('[CANcam] Device disconnected from patrol');
            setConnectedDevice(null);
            setDeviceState(null);
            setUploadProgress(null);
            setError('Device disconnected');
        });

        // Recording events
        socket.on('recording_started', (data: { fileName: string; commentListId: number }) => {
            console.log('[CANcam] Recording started:', data);
            setDeviceState((prev) =>
                prev
                    ? {
                          ...prev,
                          isRecording: true,
                          currentRecordingFileName: data.fileName,
                          recordingDuration: 0,
                      }
                    : null
            );
        });

        socket.on('recording_stopped', (data: { fileName: string; duration: number }) => {
            console.log('[CANcam] Recording stopped:', data);
            setDeviceState((prev) =>
                prev
                    ? {
                          ...prev,
                          isRecording: false,
                          currentRecordingFileName: undefined,
                          recordingDuration: undefined,
                          pendingUploads: (prev.pendingUploads || 0) + 1,
                      }
                    : null
            );
        });

        // Upload events
        socket.on('upload_progress', (data: UploadProgressPayload) => {
            setUploadProgress(data);
        });

        socket.on('upload_complete', (data: { videoId: string; fileName: string; commentListId: number }) => {
            console.log('[CANcam] Upload complete:', data);
            setUploadProgress(null);
            setDeviceState((prev) =>
                prev
                    ? {
                          ...prev,
                          pendingUploads: Math.max(0, (prev.pendingUploads || 0) - 1),
                      }
                    : null
            );
        });

        // Command errors
        socket.on('command_error', (data: { message: string }) => {
            console.error('[CANcam] Command error:', data.message);
            setError(data.message);
        });

        socket.on('error', (data: { message: string }) => {
            console.error('[CANcam] Error:', data.message);
            setError(data.message);
        });

        return () => {
            socket.disconnect();
            socketRef.current = null;
            if (codeExpiryTimerRef.current) {
                clearInterval(codeExpiryTimerRef.current);
                codeExpiryTimerRef.current = null;
            }
        };
    }, []);

    // Actions - Patrol code generation
    const generatePatrolCode = useCallback((commentListId: number, scheduleAssignmentId?: number) => {
        if (!socketRef.current?.connected) {
            setError('Not connected to CANcam service');
            return;
        }
        setError(null);
        socketRef.current.emit('generate_patrol_code', { commentListId, scheduleAssignmentId });
    }, []);

    // Actions - Patrol watching
    const watchPatrol = useCallback((commentListId: number) => {
        if (!socketRef.current?.connected) return;
        socketRef.current.emit('watch_patrol', { commentListId });
        setWatchingPatrolId(commentListId);
    }, []);

    const unwatchPatrol = useCallback(() => {
        if (!socketRef.current?.connected) return;
        if (watchingPatrolId) {
            socketRef.current.emit('unwatch_patrol', {});
            setWatchingPatrolId(null);
            setConnectedDevice(null);
            setPatrolPairingCode(null);
        }
    }, [watchingPatrolId]);

    // Actions - Device replacement
    const confirmDeviceReplacement = useCallback((approve: boolean) => {
        if (!socketRef.current?.connected || !pendingReplacement) return;
        socketRef.current.emit('confirm_device_replacement', {
            commentListId: pendingReplacement.commentListId,
            approve,
        });
        setPendingReplacement(null);
    }, [pendingReplacement]);

    // Actions - Recording controls
    const startRecording = useCallback((commentListId: number, patrolId?: number) => {
        if (!socketRef.current?.connected) {
            setError('Not connected to CANcam service');
            return;
        }
        if (!connectedDevice) {
            setError('No device connected to patrol');
            return;
        }
        setError(null);
        socketRef.current.emit('start_recording', { commentListId, patrolId });
    }, [connectedDevice]);

    const stopRecording = useCallback(() => {
        if (!socketRef.current?.connected) {
            setError('Not connected to CANcam service');
            return;
        }
        if (!connectedDevice) {
            setError('No device connected to patrol');
            return;
        }
        setError(null);
        socketRef.current.emit('stop_recording');
    }, [connectedDevice]);

    const forceUpload = useCallback(() => {
        if (!socketRef.current?.connected) {
            setError('Not connected to CANcam service');
            return;
        }
        if (!connectedDevice) {
            setError('No device connected to patrol');
            return;
        }
        setError(null);
        socketRef.current.emit('force_upload');
    }, [connectedDevice]);

    return {
        // Connection
        isConnected,
        error,

        // Patrol-based device
        connectedDevice,
        watchingPatrolId,

        // Pairing code generation
        patrolPairingCode,
        patrolCodeExpiry,
        generatePatrolCode,

        // Patrol watching
        watchPatrol,
        unwatchPatrol,

        // Device replacement
        pendingReplacement,
        confirmDeviceReplacement,

        // Device state & controls
        deviceState,
        uploadProgress,
        startRecording,
        stopRecording,
        forceUpload,
    };
}
